import { useState } from "react";
import { FadeUp, MaskedReveal, SectionLabel } from "@/components/Motion";
import { Plus, Minus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const FAQS = [
  { q: "When does the first edition arrive?", a: "Once you subscribe, your first envelope is dispatched with our next monthly send — typically within 10–14 days. Subsequent editions ship on or around the first week of every month." },
  { q: "Is this a magazine or a subscription box?", a: "Neither, exactly. It's a monthly print publication — a curated set of editorial pieces (a letter, a recipe postcard, a meal plan, a small surprise, an expert note) delivered in a paper envelope. It reads more like a magazine than it looks like a box." },
  { q: "What are the two editions?", a: "There are two editions: the Pregnancy Edition and the Motherhood Edition. Both contain the same categories of content — a letter, a Postcard from the Indian Kitchen, a weekly meal plan, an expert note and a thoughtful surprise — but the editorial material is adapted to the reader's stage." },
  { q: "How much does it cost?", a: "There are three purchase options, both editions share the same pricing: a Single Edition is ₹699, a 3-Month Subscription is ₹1,799, and a 6-Month Subscription is ₹2,999." },
  { q: "Can I gift a subscription?", a: "Yes. Please write to us at thesafemamaeditorial@gmail.com and we'll arrange a hand-written note to accompany the first envelope." },
  { q: "Do you ship internationally?", a: "For now, we ship across India. International delivery is not yet available." },
  { q: "Why don't you publish the anonymous letters online?", a: "Because a letter is a private thing. We believe the anonymous mother deserves the intimacy of paper. Publishing them online would change what they are. So we keep them off the internet, always." },
  { q: "Can I pause the subscription?", a: "Yes. Visit 'Manage Plan' on our website to pause your subscription." },
  { q: "What's included each month?", a: "Five things: an anonymous letter from a real mother, a Postcard from the Indian Kitchen tailored to your stage, a Nourishment Weekly Meal Plan, a short editorial note from a relevant professional, and a thoughtful surprise chosen with care." },
  { q: "Who is Banyan Editorials?", a: "We're a small, independent editorial studio. The Safe Mama Editorial is our first publication. More are planned, slowly." },
];

function Item({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-b border-ink/15" data-testid={`faq-item-${i}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full py-8 flex items-center justify-between text-left gap-8"
        aria-expanded={open}
      >
        <span className="font-serif text-2xl md:text-3xl leading-tight text-ink pr-6">{q}</span>
        <span className="shrink-0 w-10 h-10 rounded-full border border-ink/30 flex items-center justify-center text-ink transition-colors group-hover:bg-ink group-hover:text-cream">
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span key="m" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
                <Minus size={18} />
              </motion.span>
            ) : (
              <motion.span key="p" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
                <Plus size={18} />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-8 pr-16 font-serif text-lg md:text-xl text-ink/75 leading-relaxed max-w-2xl">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="pt-10 md:pt-16" data-testid="faq-page">
      <section className="editorial-container">
        <SectionLabel number="No. 004" label="Frequently asked" />
        <h1 className="mt-6 font-serif text-[13vw] sm:text-[9vw] md:text-[7vw] lg:text-[6rem] leading-[1] tracking-tight text-ink pb-4">
          <MaskedReveal text="Small" as="span" className="block" delay={0.1} animate />
          <MaskedReveal text="questions." as="span" className="block italic text-sage" delay={0.35} animate />
        </h1>
        <FadeUp delay={0.7}>
          <p className="mt-8 font-serif text-xl md:text-2xl leading-[1.4] text-ink/75 max-w-2xl">
            A few of the questions our readers write in with — answered gently, in one place.
          </p>
        </FadeUp>
      </section>

      <section className="mt-16 md:mt-24 editorial-container">
        <div className="max-w-3xl mx-auto">
          {FAQS.map((f, i) => (
            <Item key={i} i={i} {...f} />
          ))}
        </div>
      </section>
    </div>
  );
}
